import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Warning, CheckCircle, XCircle, HourglassSimple, Archive } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AlertsWidget = ({ wrapStyle, alertsData, language, onAction }) => {
  const archiveProject = async (pid, name) => {
    if (!window.confirm(`Archiver le chantier "${name}" ? Il ne sera plus visible dans les listes actives.`)) return;
    try {
      await axios.put(`${API_URL}/api/projects/${pid}/archive`, {}, { withCredentials: true });
      toast.success(`Chantier "${name}" archive`);
      if (onAction) onAction();
    } catch {
      toast.error('Erreur archivage');
    }
  };
  return (
  <div style={wrapStyle}>
    <div className="card" data-testid="alerts-widget">
      <div className="card-header" style={{ backgroundColor: alertsData.length > 0 ? '#FFF3E0' : '#E8F5E9' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: alertsData.length > 0 ? '#E65100' : '#2E7D32' }}>
          <Bell size={20} weight="fill" />
          {language === 'en' ? 'Smart Alerts' : 'Alertes intelligentes'}
          {alertsData.length > 0 && (
            <span style={{ backgroundColor: '#C62828', color: 'white', borderRadius: '12px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: '700', marginLeft: '4px' }}>
              {alertsData.length}
            </span>
          )}
        </h3>
      </div>
      <div style={{ padding: '16px' }}>
        {alertsData.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', color: '#2E7D32' }}>
            <CheckCircle size={24} weight="fill" />
            <span style={{ fontWeight: '500' }}>{language === 'en' ? 'No alerts - Everything is on track!' : 'Aucune alerte - Tout est en ordre !'}</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {alertsData.map((alert, idx) => {
              const severityColors = {
                danger: { bg: '#FFEBEE', border: '#EF5350', icon: '#C62828', text: '#B71C1C' },
                warning: { bg: '#FFF3E0', border: '#FF9800', icon: '#E65100', text: '#BF360C' },
                info: { bg: '#E3F2FD', border: '#42A5F5', icon: '#1565C0', text: '#0D47A1' },
              };
              const c = severityColors[alert.severity] || severityColors.info;
              const SeverityIcon = alert.severity === 'danger' ? XCircle : alert.severity === 'warning' ? Warning : HourglassSimple;
              return (
                <div
                  key={`alert-${alert.doc_key || alert.type || ''}-${idx}`}
                  data-testid={`alert-item-${idx}`}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    padding: '14px 16px', borderRadius: '8px',
                    backgroundColor: c.bg, borderLeft: `4px solid ${c.border}`,
                    transition: 'transform 0.15s', cursor: 'default'
                  }}
                >
                  <SeverityIcon size={20} weight="fill" style={{ color: c.icon, flexShrink: 0, marginTop: '1px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: c.text, lineHeight: 1.4 }}>
                      {language === 'en' ? alert.message_en : alert.message_fr}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: c.icon, marginTop: '4px', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px' }}>
                      {alert.type === 'hours_exceeded' && 'Heures'}
                      {alert.type === 'budget_exceeded' && 'Budget'}
                      {alert.type === 'expense_pending' && 'Note de frais'}
                      {alert.type === 'no_invoice_month' && 'Facturation mensuelle'}
                      {alert.type === 'project_ready_archive' && 'Chantier termine'}
                    </div>
                  </div>
                  {alert.type === 'project_ready_archive' && alert.project_id && (
                    <button
                      onClick={() => archiveProject(alert.project_id, alert.entity_name)}
                      className="btn btn-primary btn-sm"
                      style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}
                      data-testid={`archive-project-${alert.project_id}`}
                    >
                      <Archive size={14} /> Archiver
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export const EcheancesWidget = ({ wrapStyle, echeancesData, language }) => (
  <div style={wrapStyle}>
    <div className="card" data-testid="echeances-widget">
      <div className="card-header" style={{ backgroundColor: echeancesData.length > 0 ? '#FFF8E1' : '#E8F5E9' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: echeancesData.length > 0 ? '#F57F17' : '#2E7D32' }}>
          <Warning size={20} weight="fill" />
          {language === 'en' ? 'Upcoming Deadlines' : 'Echeances a venir'}
          {echeancesData.length > 0 && (
            <span style={{ backgroundColor: '#E65100', color: 'white', borderRadius: '12px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: '700' }}>
              {echeancesData.length}
            </span>
          )}
        </h3>
      </div>
      <div style={{ padding: '16px' }}>
        {echeancesData.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', color: '#2E7D32' }}>
            <CheckCircle size={24} weight="fill" />
            <span style={{ fontWeight: '500' }}>{language === 'en' ? 'No upcoming deadlines' : 'Aucune echeance a venir'}</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {echeancesData.slice(0, 8).map((item, idx) => {
              const colors = { danger: { bg: '#FFEBEE', border: '#EF5350', text: '#B71C1C' }, warning: { bg: '#FFF3E0', border: '#FF9800', text: '#BF360C' }, info: { bg: '#E3F2FD', border: '#42A5F5', text: '#0D47A1' } };
              const c = colors[item.severity] || colors.info;
              const typeLabels = { doc: 'Document', cdd: 'CDD', medical: 'Visite med.' };
              return (
                <div key={`ech-${item.type || ''}-${item.label || ''}-${idx}`} data-testid={`echeance-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '6px', backgroundColor: c.bg, borderLeft: `3px solid ${c.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: c.text }}>{item.label}</div>
                    <div style={{ fontSize: '0.75rem', color: '#5E5F66', marginTop: '2px' }}>{item.detail}</div>
                  </div>
                  <span style={{ fontSize: '0.7rem', fontWeight: '700', color: c.text, backgroundColor: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>{typeLabels[item.type] || item.type}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </div>
);
